import { chakra, VStack } from '@chakra-ui/react';
import React from 'react';

const Background = chakra('div', {
  base: {
    width: '100px',
    height: '100px',
    backgroundColor: 'dark',
    borderRadius: 'double',

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
Background.displayName = 'LoaderBackground';

const Backdrop = chakra('div', {
  base: {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100vw',
    height: '100dvh',
    backgroundColor: 'dark/75',
    zIndex: 1000,

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
Backdrop.displayName = 'LoaderBackdrop';

const Positioner = chakra('div', {
  base: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
  },
});
Positioner.displayName = 'LoaderPositioner';

const Spinner = chakra('div', {
  base: {
    width: '50px',
    height: '50px',

    position: 'relative',

    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    animationStyle: 'spinner-rotate',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
    animationDuration: '3.5s',

    background: `conic-gradient(
        {colors.dark} 0deg,
        {colors.dark} var(--pulser-start),
        {colors.halton.green} var(--pulser-start),
        {colors.halton.blue} calc(calc(calc(var(--pulser-end) - var(--pulser-start)) * 0.33) + var(--pulser-start)),
        {colors.halton.blue} calc(calc(calc(var(--pulser-end) - var(--pulser-start)) * 0.67) + var(--pulser-start)),
        {colors.halton.green} var(--pulser-end),
        {colors.dark} var(--pulser-end),
        {colors.dark} 360deg
      )`,
  },
});
Spinner.displayName = 'LoaderSpinner';

const PulserCap = chakra('div', {
  base: {
    '--pulser-start': '0deg',

    animationTimingFunction: 'linear',
    animationDuration: '3.5s',
    animationIterationCount: 'infinite',

    position: 'absolute',

    width: '50px',
    height: '50px',

    _before: {
      content: '""',
      position: 'absolute',
      width: '5px',
      height: '5px',
      borderRadius: '50%',
      left: '0px',
      top: '50%',
      transform: 'translateY(-50%)',
    },
  },
  variants: {
    position: {
      end: {
        animationStyle: 'spinner-pulser-end-rotate',
        transform: 'rotateZ(var(--pulser-end))',

        _before: {
          background: `linear-gradient(
            180deg,
            {colors.halton.green} 0%,
            {colors.halton.green} 50%,
            transparent 50%,
            transparent 100%
          )`,
        },
      },
      start: {
        animationStyle: 'spinner-pulser-start-rotate',
        transform: 'rotateZ(var(--pulser-start))',

        _before: {
          background: `linear-gradient(
            0deg,
            {colors.halton.green} 0%,
            {colors.halton.green} 50%,
            transparent 50%,
            transparent 100%
          )`,
        },
      },
    },
  },
});
PulserCap.displayName = 'LoaderPulserCap';

const SpinnerCenter = chakra('div', {
  base: {
    width: '40px',
    height: '40px',
    backgroundColor: 'dark',
    borderRadius: '50%',
  },
});
SpinnerCenter.displayName = 'LoaderSpinnerCenter';

const LoaderText = chakra('div', {
  base: {
    backgroundColor: 'dark',
    padding: '10px 20px',
    borderRadius: 'double',
  },
});
LoaderText.displayName = 'LoaderText';

interface LoaderProps {
  fullscreen?: boolean;
  cover?: boolean;
  text?: string;
}

export const Loader: React.FC<LoaderProps> = ({ fullscreen, cover, text }) => {
  const Wrapper = fullscreen ? Backdrop : cover ? Positioner : React.Fragment;
  return (
    <Wrapper>
      <VStack gap="20px">
        <Background>
          <Spinner>
            <PulserCap position="start" />
            <PulserCap position="end" />
            <SpinnerCenter />
          </Spinner>
        </Background>
        {text && <LoaderText>{text}</LoaderText>}
      </VStack>
    </Wrapper>
  );
};
